import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../../utils/structuredData';
import './ServicePage.css';

const WordPressDevelopment = () => {
  const serviceStructuredData = serviceSchema(
    'WordPress Development',
    'Custom WordPress solutions tailored to your needs. We build themes, plugins, and full-featured websites that are fast, secure, and easy to manage.',
    'WordPress Development'
  );

  const breadcrumbStructuredData = breadcrumbSchema([
    { name: 'Home', url: 'https://www.gettechupdate.com' },
    { name: 'Services', url: 'https://www.gettechupdate.com/#services' },
    { name: 'WordPress Development', url: 'https://www.gettechupdate.com/services/wordpress-development' }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEO
        title="WordPress Development Services in Bhopal, Indore | Budget Website Development"
        description="Affordable WordPress development services in Bhopal, Indore, Delhi, Mumbai, Bangalore, and across India. Custom WordPress themes, plugins, WooCommerce development, and website development in budget."
        keywords="WordPress development, WordPress development in Bhopal, WordPress development in Indore, website development in budget, affordable WordPress development, WordPress themes, WordPress plugins, WooCommerce development, WordPress customization, WordPress optimization, budget website development"
        url="https://www.gettechupdate.com/services/wordpress-development"
        structuredData={combinedSchema}
      />
      <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-icon-large">
            <i className="fab fa-wordpress"></i>
          </div>
          <h1>WordPress Development</h1>
          <p className="service-hero-subtitle">
            Custom WordPress solutions tailored to your needs. Fast, secure, and easy-to-manage websites that grow with your business.
          </p>
        </div>
      </div>

      <div className="service-content">
        <section className="service-section">
          <h2>Professional WordPress Development Services</h2>
          <p>
            WordPress powers over 40% of all websites on the internet. Our expert team creates custom WordPress 
            solutions that are fast, secure, and perfectly aligned with your business goals. Whether you need a 
            simple blog or a complex e-commerce platform, we deliver WordPress websites that exceed expectations.
          </p>
        </section>

        <section className="service-section">
          <h2>Our WordPress Services</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-paint-brush"></i>
              <h3>Custom Theme Development</h3>
              <p>Bespoke WordPress themes designed and developed from scratch to match your brand identity and requirements.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-plug"></i>
              <h3>Plugin Development</h3>
              <p>Custom WordPress plugins to add specific functionality and features unique to your website needs.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shopping-cart"></i>
              <h3>WooCommerce Development</h3>
              <p>Full-featured e-commerce solutions using WooCommerce with customizations for your business model.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-tachometer-alt"></i>
              <h3>Performance Optimization</h3>
              <p>Speed optimization, caching setup, and performance tuning to ensure your WordPress site loads quickly.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shield-alt"></i>
              <h3>Security Hardening</h3>
              <p>Implementing security best practices, SSL certificates, and protection against vulnerabilities and attacks.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-sync-alt"></i>
              <h3>Migration & Updates</h3>
              <p>Seamless WordPress migrations, updates, and maintenance to keep your site current and secure.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>WordPress Technologies & Tools</h2>
          <div className="tech-stack">
            <div className="tech-item">
              <i className="fab fa-wordpress"></i>
              <span>WordPress Core</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-shopping-bag"></i>
              <span>WooCommerce</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-puzzle-piece"></i>
              <span>ACF</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-code"></i>
              <span>PHP</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-js"></i>
              <span>JavaScript</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>MySQL</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-paint-brush"></i>
              <span>Elementor</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-layer-group"></i>
              <span>Gutenberg</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>WordPress Solutions We Build</h2>
          <div className="app-types">
            <div className="app-type-card">
              <i className="fas fa-store"></i>
              <h3>E-commerce Stores</h3>
              <p>Complete online stores with WooCommerce, payment gateways, inventory management, and order tracking.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-blog"></i>
              <h3>Blog & Magazine Sites</h3>
              <p>Content-rich websites with custom post types, categories, tags, and advanced content management.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-building"></i>
              <h3>Corporate Websites</h3>
              <p>Professional business websites with custom layouts, portfolios, and service showcases.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-graduation-cap"></i>
              <h3>Learning Management</h3>
              <p>Educational platforms with course management, student portals, and learning tracking.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-calendar-alt"></i>
              <h3>Event Management</h3>
              <p>Event booking systems with registration, ticketing, and calendar integration.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-users"></i>
              <h3>Membership Sites</h3>
              <p>Membership platforms with user registration, subscription management, and content restrictions.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Development Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Planning & Strategy</h3>
              <p>Understanding your requirements, target audience, and business goals to create a comprehensive WordPress strategy.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Design & Customization</h3>
              <p>Creating custom designs or customizing existing themes to match your brand and user experience goals.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Development</h3>
              <p>Building your WordPress site with clean, optimized code, custom functionality, and plugin integration.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Content Integration</h3>
              <p>Setting up content structure, importing content, and configuring SEO settings for optimal visibility.</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Testing & Optimization</h3>
              <p>Thorough testing, performance optimization, security checks, and cross-browser compatibility testing.</p>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <h3>Launch & Training</h3>
              <p>Deployment to production, final checks, and training on how to manage and update your WordPress site.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Key Features We Implement</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Custom Theme Development</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Plugin Customization</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>WooCommerce Setup</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Payment Gateway Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>SEO Optimization</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Responsive Design</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Contact Forms</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Multi-language Support</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>User Management</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Blog Functionality</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Media Library</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Security Hardening</span>
            </div>
          </div>
        </section>

        <section className="service-cta">
          <h2>Ready to Build Your WordPress Site?</h2>
          <p>Let's create a powerful WordPress website for your business</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get A Free Quote</Link>
            <Link to="/contact" className="btn-secondary">Schedule a Consultation</Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default WordPressDevelopment;

